/** Room booking domain types (availability exported via per-room iCal feeds). */
export const BOOKING_SOURCES = [
    'airbnb',
    'expedia',
    'booking',
    'web',
    'instagram',
    'on_site',
    'other',
];
export const BOOKING_STATUSES = ['pending', 'confirmed', 'cancelled', 'rejected'];
export const BOOKING_PAYMENT_OPTIONS = ['on_site', 'online', 'free'];
export const BOOKING_PAYMENT_STATUSES = ['unpaid', 'pending', 'paid', 'failed'];
export const PHONE_COUNTRIES = [
    { iso: 'AM', dial: '374' },
    { iso: 'GE', dial: '995' },
    { iso: 'RU', dial: '7' },
    { iso: 'IR', dial: '98' },
    { iso: 'TR', dial: '90' },
    { iso: 'AE', dial: '971' },
    { iso: 'IL', dial: '972' },
    { iso: 'UA', dial: '380' },
    { iso: 'BY', dial: '375' },
    { iso: 'KZ', dial: '7' },
    { iso: 'US', dial: '1' },
    { iso: 'GB', dial: '44' },
    { iso: 'DE', dial: '49' },
    { iso: 'FR', dial: '33' },
    { iso: 'IT', dial: '39' },
    { iso: 'ES', dial: '34' },
    { iso: 'PL', dial: '48' },
    { iso: 'NL', dial: '31' },
    { iso: 'BE', dial: '32' },
    { iso: 'AT', dial: '43' },
    { iso: 'CH', dial: '41' },
    { iso: 'CN', dial: '86' },
    { iso: 'IN', dial: '91' },
    { iso: 'KR', dial: '82' },
    { iso: 'JP', dial: '81' },
];
export const DEFAULT_PHONE_COUNTRY_ISO = 'AM';
/** Digits only for the national number field. */
export function bookingPhoneDigits(raw) {
    return raw.replace(/\D/g, '');
}
export function phoneCountryByIso(iso) {
    return PHONE_COUNTRIES.find((c) => c.iso === iso) ?? PHONE_COUNTRIES[0];
}
/** Persist as E.164 (`+374…`). */
export function composeBookingPhone(iso, national) {
    const digits = bookingPhoneDigits(national);
    if (!digits)
        return '';
    return `+${phoneCountryByIso(iso).dial}${digits}`;
}
/** @deprecated Use composeBookingPhone. Treats the whole string as digits after +. */
export function formatBookingPhone(raw) {
    const digits = bookingPhoneDigits(raw);
    return digits ? `+${digits}` : '';
}
/** Split a stored E.164 number into country ISO + national digits. Defaults to Armenia. */
export function parseStoredBookingPhone(raw) {
    const digits = bookingPhoneDigits(raw);
    if (!digits)
        return { iso: DEFAULT_PHONE_COUNTRY_ISO, national: '' };
    const ranked = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
    for (const c of ranked) {
        if (digits.startsWith(c.dial) && digits.length > c.dial.length) {
            return { iso: c.iso, national: digits.slice(c.dial.length) };
        }
    }
    return { iso: DEFAULT_PHONE_COUNTRY_ISO, national: digits };
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function parseNotifyEmails(raw) {
    const parts = Array.isArray(raw)
        ? raw.map((v) => String(v))
        : typeof raw === 'string'
            ? raw.split(/[,;\n]+/)
            : [];
    const out = [];
    for (const p of parts) {
        const email = p.trim().toLowerCase();
        if (EMAIL_RE.test(email) && !out.includes(email))
            out.push(email);
    }
    return out;
}
export function isValidGuestEmail(raw) {
    return EMAIL_RE.test(raw.trim());
}
export function phoneCountryLabel(iso, locale) {
    const dial = phoneCountryByIso(iso).dial;
    try {
        const name = new Intl.DisplayNames([locale], { type: 'region' }).of(iso);
        return `${name ?? iso} +${dial}`;
    }
    catch {
        return `${iso} +${dial}`;
    }
}
/** Display integer AMD amounts with thousands commas (e.g. 12,500). */
export function formatBookingAmount(n) {
    if (!(n > 0))
        return '';
    return Math.round(n).toLocaleString('en-US');
}
/** Parse a thousands-comma amount string back to an integer. */
export function parseBookingAmount(raw) {
    const digits = raw.replace(/\D/g, '');
    return digits ? Number(digits) : 0;
}
export function parseBookingFiscal(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const r = raw;
    const fiscal = (typeof r.fiscal === 'string' && r.fiscal) ||
        (typeof r.FISCAL === 'string' && r.FISCAL) ||
        null;
    if (!fiscal && r.total == null && r.crn == null)
        return null;
    return {
        fiscal,
        rseq: typeof r.rseq === 'number' ? r.rseq : r.rseq != null ? Number(r.rseq) : null,
        total: typeof r.total === 'number' ? r.total : r.total != null ? Number(r.total) : null,
        crn: typeof r.crn === 'string' ? r.crn : null,
        tin: typeof r.tin === 'string' ? r.tin : null,
        time: typeof r.time === 'number' ? r.time : r.time != null ? Number(r.time) : null,
        qr: typeof r.qr === 'string' ? r.qr : null,
        lottery: typeof r.lottery === 'string' ? r.lottery : null,
    };
}
/** Paid on-site via HDM (cash/card) — refund must go through the fiscal return receipt flow. */
export function bookingRequiresFiscalRefund(input) {
    if (input.refunded_at)
        return false;
    if (input.payment_status !== 'paid')
        return false;
    if (input.payment_method !== 'cash' && input.payment_method !== 'card')
        return false;
    const fiscal = parseBookingFiscal(input.fiscal_receipt);
    return !!(fiscal?.fiscal && fiscal.crn && fiscal.rseq != null);
}
//# sourceMappingURL=booking.js.map